import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import authRecoveryService from '../../../services/authRecoveryService';

const BrokenAuthStatesPanel = ({ refreshTrigger, onRepairComplete }) => {
  const [authStates, setAuthStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedState, setSelectedState] = useState(null);
  const [repairMethod, setRepairMethod] = useState('manual_clear');
  const [notes, setNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [bulkSelection, setBulkSelection] = useState([]);

  useEffect(() => {
    loadAuthStates();
  }, [refreshTrigger]);

  const loadAuthStates = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await authRecoveryService?.getBrokenAuthStates();
      setAuthStates(data);
    } catch (err) {
      setError(err?.message || 'Failed to load broken auth states');
    } finally {
      setLoading(false);
    }
  };

  const handleRepair = async () => {
    if (!selectedState) return;
    if (!notes?.trim()) {
      setError('Repair notes are required for audit trail');
      return;
    }

    try {
      setProcessing(true);
      setError('');
      await authRecoveryService?.clearBrokenAuthState(
        selectedState?.userId,
        repairMethod,
        notes
      );
      alert(`Auth state cleared successfully for ${selectedState?.email}`);
      setSelectedState(null);
      setNotes('');
      await loadAuthStates();
      onRepairComplete?.();
    } catch (err) {
      setError(err?.message || 'Failed to clear auth state');
    } finally {
      setProcessing(false);
    }
  };

  const handleBulkRepair = async () => {
    if (bulkSelection?.length === 0) return;
    const bulkNotes = prompt('Enter repair notes for bulk operation:');
    if (!bulkNotes) return;

    try {
      setLoading(true);
      setError('');
      const results = await authRecoveryService?.bulkClearAuthStates(
        bulkSelection,
        repairMethod,
        bulkNotes
      );
      const successCount = results?.filter(r => r?.success)?.length;
      alert(`Successfully cleared ${successCount} of ${bulkSelection?.length} auth states`);
      setBulkSelection([]);
      await loadAuthStates();
      onRepairComplete?.();
    } catch (err) {
      setError(err?.message || 'Bulk repair failed');
    } finally {
      setLoading(false);
    }
  };

  const toggleBulkSelection = (userId) => {
    setBulkSelection(prev =>
      prev?.includes(userId)
        ? prev?.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const repairMethods = [
    { value: 'manual_clear', label: 'Manual Clear' },
    { value: 'session_reset', label: 'Session Reset' },
    { value: 'profile_sync', label: 'Profile Sync' },
    { value: 'force_logout', label: 'Force Logout' }
  ];

  if (loading && authStates?.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader2" className="w-6 h-6 text-emerald-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Broken Authentication States
        </h3>
        {bulkSelection?.length > 0 && (
          <Button size="sm" onClick={handleBulkRepair}>
            <Icon name="Zap" size={14} className="mr-1" />
            Bulk Repair ({bulkSelection?.length})
          </Button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-slate-700 border-b border-gray-200 dark:border-slate-600">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={bulkSelection?.length === authStates?.filter(s => !s?.repairedAt)?.length && authStates?.filter(s => !s?.repairedAt)?.length > 0}
                    onChange={(e) => {
                      if (e?.target?.checked) {
                        setBulkSelection(authStates?.filter(s => !s?.repairedAt)?.map(s => s?.userId));
                      } else {
                        setBulkSelection([]);
                      }
                    }}
                    className="rounded border-gray-300 dark:border-slate-600"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">User</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Error Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Detected</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
              {authStates?.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-sm text-gray-600 dark:text-gray-400">
                    <Icon name="CheckCircle" className="w-12 h-12 mx-auto mb-2 text-green-500" />
                    <p>No broken auth states detected</p>
                  </td>
                </tr>
              ) : (
                authStates?.map((state) => (
                  <tr key={state?.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors">
                    <td className="px-4 py-3">
                      {!state?.repairedAt && (
                        <input
                          type="checkbox"
                          checked={bulkSelection?.includes(state?.userId)}
                          onChange={() => toggleBulkSelection(state?.userId)}
                          className="rounded border-gray-300 dark:border-slate-600"
                        />
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{state?.fullName || 'Unknown'}</p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">{state?.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 text-xs font-medium rounded bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">
                        {state?.errorType?.replace('_', ' ')?.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-gray-900 dark:text-white">
                        {new Date(state?.detectedAt)?.toLocaleString()}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      {state?.repairedAt ? (
                        <span className="px-2 py-1 text-xs font-bold rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                          REPAIRED
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-bold rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
                          PENDING
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {!state?.repairedAt && (
                        <Button
                          size="sm"
                          onClick={() => setSelectedState(state)}
                        >
                          <Icon name="Wrench" size={14} className="mr-1" />
                          Repair
                        </Button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Repair Modal */}
      {selectedState && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-2xl w-full">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Clear Broken Auth State</h3>
              <button
                onClick={() => {
                  setSelectedState(null);
                  setNotes('');
                  setError('');
                }}
                className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <Icon name="AlertTriangle" className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">Critical System Operation</p>
                    <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                      This will clear the broken authentication state for {selectedState?.email}. Provide detailed notes for audit trail.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Repair Method
                </label>
                <select
                  value={repairMethod}
                  onChange={(e) => setRepairMethod(e?.target?.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                >
                  {repairMethods?.map(method => (
                    <option key={method?.value} value={method?.value}>
                      {method?.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Repair Notes (Required)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e?.target?.value)}
                  placeholder="Explain why this repair is necessary and what steps were taken..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setSelectedState(null);
                    setNotes('');
                    setError('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleRepair}
                  disabled={processing || !notes?.trim()}
                  loading={processing}
                >
                  <Icon name="Wrench" size={14} className="mr-1" />
                  Clear Auth State
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BrokenAuthStatesPanel;